import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import { processKeys } from '../query-keys';
import {
  addProcessContract,
  editProcessContract,
  deleteProcessContract,
  duplicateProcessContract,
  type AddProcessRequest,
  type AddProcessResponse,
  type EditProcessRequest,
  type EditProcessResponse,
  type DeleteProcessResponse,
  type DuplicateProcessRequest,
  type DuplicateProcessResponse
} from '@bliks-io/contracts';

/**
 * Create process mutation
 *
 * Creates a new process.
 * - Invalidates cross-entity dependents of `process` (lists, map, tasks,
 *   dashboards, chains, organization) via the cache-invalidation registry.
 * - Returns the newly created process
 */
export function useCreateProcess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddProcessRequest): Promise<AddProcessResponse> => {
      const { data: response } = await apiClient.post<AddProcessResponse>(
        addProcessContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'process');
    }
  });
}

/**
 * Update process mutation
 *
 * Updates an existing process by ID.
 * - Invalidates cross-entity dependents of `process` so populated copies
 *   embedded in tasks, dashboards, recordings etc. refresh.
 * - Returns the updated process
 */
export function useUpdateProcess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: EditProcessRequest;
    }): Promise<EditProcessResponse> => {
      const path = editProcessContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<EditProcessResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'process');
    }
  });
}

/**
 * Duplicate process mutation
 *
 * Creates a snapshot copy of an existing process under a new name.
 * - Invalidates cross-entity dependents of `process` so lists refresh.
 * - Returns the newly created process (the copy)
 */
export function useDuplicateProcess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceId,
      data
    }: {
      sourceId: string;
      data: DuplicateProcessRequest;
    }): Promise<DuplicateProcessResponse> => {
      const path = duplicateProcessContract.path.replace(':id', sourceId);
      const { data: response } = await apiClient.post<DuplicateProcessResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'process');
    }
  });
}

/**
 * Delete process mutation
 *
 * Deletes a process by ID.
 * - Invalidates cross-entity dependents of `process`.
 * - Removes process detail from cache.
 */
export function useDeleteProcess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteProcessResponse> => {
      const path = deleteProcessContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteProcessResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      invalidateEntity(queryClient, 'process');
      queryClient.removeQueries({ queryKey: processKeys.detail(id) });
    }
  });
}
